import { useEffect, useMemo, useRef, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
	AppButton,
	Change,
	CountBadge,
	Divider,
	DotMatrix,
	Input,
	ListItem,
	PageHeader,
	Pagination,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	Tab,
	TabList,
	Tabs,
	Tooltip,
	TwoPaneMenu,
	useEnterExit
} from '@clickguard/design-system'
import type { TwoPaneMenuCategory } from '@clickguard/design-system'
import AlertCircle from '@untitled-ui/icons-react/build/esm/AlertCircle'
import CheckCircle from '@untitled-ui/icons-react/build/esm/CheckCircle'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'
import FileCheck02 from '@untitled-ui/icons-react/build/esm/FileCheck02'
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines'
import Minus from '@untitled-ui/icons-react/build/esm/Minus'
import MinusCircle from '@untitled-ui/icons-react/build/esm/MinusCircle'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import RefreshCw01 from '@untitled-ui/icons-react/build/esm/RefreshCw01'
import SearchMd from '@untitled-ui/icons-react/build/esm/SearchMd'
import ShieldOff from '@untitled-ui/icons-react/build/esm/ShieldOff'
import { GTable } from './components/form-table'
import { GTableColumn } from './components/form-table/types'
import { PageTitle } from 'utils/page-title'
import {
	CrmAccount,
	FormRecord,
	NewFormDraft,
	PAGE_SIZE_OPTIONS,
	VerdictTotals,
	botLevelOf,
	formatCount,
	formatShare,
	getVerdictSeries,
	previousVerdictTotals,
	useFormSpam,
	useFlowHeight,
	useSwapHold,
	useSwapKey,
	verdictTotals
} from './form-spam-data'
import type { Counts } from '../lead-forms/api'
import { useLeadFormStats } from '../lead-forms/use-lead-form-stats'
import { FormSpamShell } from './components/form-spam-shell'
import { FormSpamEmptyState } from './components/form-spam-empty-state'
import { LinkFormModal } from './components/link-form-modal'
import { LinkFormCardsModal } from './components/link-form-cards-modal'
import { FormDetailDrawer } from './components/form-detail-drawer'
import { SubmissionsTable } from './components/submissions-table'
import {
	DashboardRange,
	TrafficQualityTray
} from './components/traffic-quality-tray'

// R138 (Gustavo pin 40, 2026-08-27): two quick filters. Active and Paused
// left the strip; the Status entry in the Filters menu still cuts on them.
// R145 (Esteban, 2026-08-28): one pill. All forms went too, and Needs setup
// is a toggle, on or off. The table with the toggle off IS all forms, so a
// pill that said so was a second control for the same state.
// R291 (Esteban, 10 Sep): no pill. The Protection status column left the
// table, so the Needs setup toggle that cut on it left the toolbar with it.
// The drawer header still carries its own Needs setup badge (R260).

/**
 * R105. The two ranges the page header offers, and how many days each reads.
 *
 * Every series in the mock is 30 days long and every one of them maps the same
 * day array, so a range is a tail slice and the two views always agree on the
 * days they share.
 */
const RANGE_TABS: Array<{ key: DashboardRange; label: string; days: number }> = [
	{ key: '7d', label: '7 Days', days: 7 },
	{ key: '30d', label: '30 Days', days: 30 }
]

// R179 (Esteban, 1 Sep): "didn't we have the number of rows you wanted to
// show?". The footer grows the rows-per-page picker back and the table opens
// at 5 rows. PAGE_SIZE only seeds the state; the picker drives it after.
const PAGE_SIZE = 5
// R270: the rows-per-page stops moved to `form-spam-data`, shared with the
// drawer feed.

/* `needsSetup` lived here until R260. It now comes from form-spam-data, where
   the drawer badge and its red setup lines read it. This page stopped reading
   it in R291, with the status column and the Needs setup pill. */

/**
 * R104: the four form facets, in the order the reader meets them. They used to
 * be `CardFilter` tiles across the top of the page. The tray took that place,
 * so the facets moved into the table card as `FilterPill`s, the same control
 * the drawer puts over its submissions feed. A pill inside the card it filters
 * cannot be read as a control on the charts above it.
 *
 * R110 moved them again, down one row: they now sit in the toolbar, to the
 * right of Filters. A pill and the filter menu do the same job to the same
 * table, so they belong on the same line.
 *
 * R138 (Gustavo pin 40): two pills, not four. Active and Paused went out of
 * the strip; the Status entry in the Filters menu still cuts on them.
 *
 * R145: one pill, `Needs setup`, and it is a toggle. See `needsOnly`.
 *
 * R291 (Esteban, 10 Sep): no pill. The toolbar holds the search box and the
 * Filters menu, and nothing else cuts the table.
 */

/**
 * The Bot Verification and the Email Validation cells share one shape: a 16px
 * state icon and a short word. The states come from the Figma page (37:25131):
 * check-circle On, minus-circle Monitoring, shield-off Off.
 *
 * R294 (Gustavo, 4 Sep, pin #74, "add example of a line with an issue
 * flagged"): a fourth state, `lost`. The Flag on CRM cell reads Access lost
 * when the CRM revoked the login the form pushes to, in the same red the
 * drawer's Access lost pill (R279) wears. Two words, an icon, no pill: the
 * row shows its issue in the cell that owns it.
 */
type CheckState = 'on' | 'monitoring' | 'off' | 'lost'

const CHECK_META: Record<
	CheckState,
	{ label: string; Icon: typeof CheckCircle; className: string }
> = {
	on: { label: 'On', Icon: CheckCircle, className: 'cg-text-success' },
	monitoring: {
		label: 'Monitoring',
		Icon: MinusCircle,
		className: 'cg-text-brand'
	},
	off: { label: 'Off', Icon: ShieldOff, className: 'cg-text-text-secondary' },
	lost: { label: 'Access lost', Icon: AlertCircle, className: 'cg-text-danger' }
}

/**
 * R142 (Gustavo, pin 42): a count and its change against the period before.
 *
 * The tone is ALWAYS the brand purple (`trend="neutral"`), at his ask: a rise
 * in Form submissions is neither good nor bad, and a rise in Flagged or
 * Blocked reads both ways, so no cell paints green or red. Only the arrow
 * turns. DS `Change`, arrow type, sm, the same "vs previous period" sentence
 * the drawer legend carries, and the same zigzag glyph its rows use, so the
 * page has one growth glyph. The earlier period is drawn from the seed, so a
 * form with no earlier traffic shows no change, and the slot never flips at
 * runtime: the count and its change live and die with the row, and the row's
 * own fade carries them in.
 */
const countsToVerdict = (counts?: Counts): VerdictTotals => ({
	total: counts?.submissions ?? 0,
	challenge: counts?.flagged ?? 0,
	block: counts?.blocked ?? 0,
	allow: Math.max(0, (counts?.submissions ?? 0) - (counts?.flagged ?? 0))
})

const CountCell = ({ value, prev }: { value: number; prev: number }) => (
	<span className="inline-flex items-center gap-2">
		<span className="text-[14px] leading-[18px] cg-text-text-primary">
			{formatCount(value)}
		</span>
		{prev > 0 && value === prev ? (
			// R313 (Gustavo, 11 Sep): "0.0% with no growth indicator, just a
			// straight line (still purple color tho)". The DS `Change` only knows
			// up and down, so a flat move draws the same sm chip by hand from the
			// DS classes (brand ink, 12/16 semibold, 4px gap, 14px icon) with the
			// untitled-ui `Minus` where the trend arrow would sit. DS gap owed:
			// `Change direction="flat"`.
			<Tooltip content="vs previous period" portal>
				<span className="cg-inline-flex cg-items-center cg-gap-1 cg-font-sans cg-text-text-xs cg-font-semibold cg-text-text-brand">
					<Minus aria-hidden="true" className="cg-h-3.5 cg-w-3.5 cg-shrink-0" />
					{formatShare(0, prev)}
				</span>
			</Tooltip>
		) : prev > 0 ? (
			<Tooltip content="vs previous period" portal>
				<Change
					trend="neutral"
					type="trend"
					size="sm"
					direction={value > prev ? 'up' : 'down'}
					// R293 (Gustavo, 4 Sep, pin #73): the sign rides the number, the way
					// the drawer legend's `+5.3 p.p.` does, so the arrow is not the only
					// thing that says which way the count moved.
					value={`${value > prev ? '+' : '-'}${formatShare(
						Math.abs(value - prev),
						prev
					)}`}
				/>
			</Tooltip>
		) : null}
	</span>
)

/**
 * The bot degree drives the Bot Verification cell, through the same helper the
 * settings use.
 *
 * R123 (Gustavo's review, 2026-08-26): the cell reads On, Monitoring or Off.
 * Between 2026-08-20 and R123 it named the DEGREE (CAPTCHA or Blocking), at
 * Ralph's ask in the 08-20 product sync. Gustavo's later review asks for the
 * plain word: a form that acts on bots is On, and Monitoring is the one case
 * where the check runs but stops nobody. The degree still lives in the form's
 * settings, one click away.
 */
const botCheckState = (form: FormRecord): CheckState => {
	if (!form.captcha) return 'off'
	return botLevelOf(form) === 'monitor' ? 'monitoring' : 'on'
}

/**
 * R294 (Gustavo, pins #72 and #74): the Flag on CRM cell.
 *
 * The same derivation the drawer's Setup tab uses (R262): `crmFlag` is what
 * the wizard wrote, and a form without it follows the connection, so a bound
 * CRM reads as On. A form with no CRM, or with the switch off, reads Off. A
 * bound login the provider revoked (`CrmAccount.status === 'lost'`, R279)
 * reads Access lost: the switch is on, but nothing reaches the CRM.
 */
const crmFlagState = (form: FormRecord, accounts: CrmAccount[]): CheckState => {
	const provider = form.crm.brevo ? 'brevo' : form.crm.hubspot ? 'hubspot' : null
	if (!provider) return 'off'
	if (form.crmFlag === false) return 'off'
	const account = accounts.find((a) => a.id === form.crm[provider])
	return account?.status === 'lost' ? 'lost' : 'on'
}

const CheckCell = ({ state }: { state: CheckState }) => {
	const { label, Icon, className } = CHECK_META[state]
	return (
		<div className="flex items-center gap-2">
			<Icon className={`h-4 w-4 ${className}`} />
			<span className="whitespace-nowrap text-[14px] leading-[18px] cg-text-text-primary">
				{label}
			</span>
		</div>
	)
}

/**
 * R335 (Gustavo, 14 Sep, pin #110, "Should we make this Access lost more
 * visible?"): the Flag on CRM cell sits eighth of nine, so on most widths it
 * scrolls under the frozen View column. His option 2: an alert beside View,
 * which never scrolls. The 16px slot renders on every row, so the button
 * never moves; only a lost row shows the icon, the same red AlertCircle the
 * cell wears. It crossfades in and out (200ms opacity + 2px lift) when the
 * state flips, e.g. the drawer's Flag on CRM switch, so no hard cut. The
 * tooltip says why, since the cell that says so may be off screen. The
 * Tooltip wraps the icon INSIDE the slot, so the slot element stays mounted
 * across the flip and the exit fade plays.
 */
const LostFlag = ({ lost }: { lost: boolean }) => {
	const icon = <AlertCircle className="block h-4 w-4 cg-text-danger" />
	return (
		<span
			data-testid="crm-lost-flag"
			role="img"
			aria-label="CRM access lost"
			aria-hidden={!lost}
			className={`inline-flex h-4 w-4 shrink-0 items-center justify-center transition duration-200 motion-reduce:transition-none ${
				lost
					? 'translate-y-0 opacity-100'
					: 'pointer-events-none -translate-y-0.5 opacity-0'
			}`}
		>
			{lost ? (
				<Tooltip content="CRM access lost" position="top" portal>
					{icon}
				</Tooltip>
			) : (
				icon
			)}
		</span>
	)
}

/**
 * The filter facets mirror the three stateful columns and reuse the labels
 * the cells render. Picks OR within a category and AND across categories.
 *
 * R291 (Esteban, 10 Sep): the `status` group (Active / Paused, R150) left
 * with the Protection status column. A filter on a fact the table no longer
 * shows would hide rows for a reason the reader cannot see.
 *
 * R294 (Gustavo, pins #72 and #87): the groups carry the three short column
 * names, and the Flag on CRM group is new ("Add Flag on CRM here as well").
 * Its third item, Access lost, is the state the cell renders for a revoked
 * login, so a reader can cut the list to the rows with that issue.
 */
const FILTER_CATEGORIES: TwoPaneMenuCategory[] = [
	{
		id: 'botCheck',
		label: 'Block bots',
		items: [
			{ id: 'on', label: 'On' },
			{ id: 'monitoring', label: 'Monitoring' },
			{ id: 'off', label: 'Off' }
		]
	},
	{
		id: 'emailValidation',
		label: 'Protect Ads',
		items: [
			{ id: 'on', label: 'On' },
			{ id: 'off', label: 'Off' }
		]
	},
	{
		id: 'crmFlag',
		label: 'Flag on CRM',
		items: [
			{ id: 'on', label: 'On' },
			{ id: 'off', label: 'Off' },
			{ id: 'lost', label: 'Access lost' }
		]
	}
]

const matchesFilters = (
	form: FormRecord,
	picked: Record<string, string[]>,
	accounts: CrmAccount[]
) => {
	const bot = picked.botCheck
	if (bot?.length && !bot.includes(botCheckState(form))) return false
	const email = picked.emailValidation
	if (email?.length && !email.includes(form.emailValidation ? 'on' : 'off')) {
		return false
	}
	const crm = picked.crmFlag
	if (crm?.length && !crm.includes(crmFlagState(form, accounts))) return false
	return true
}

/**
 * The Form Spam list, built to Figma node 37:25131.
 *
 * R341 (2026-09-16, Gustavo on Slack, then Esteban): this page NEVER shows the
 * tracking code install. It has two states and no third one: the first-run
 * empty state that asks for the first form, and the dashboard with the forms.
 *
 * The install belongs to account setup, not to Lead Forms. A customer who
 * reaches this page already has the code: without it the rail locks the Lead
 * Forms row (see `use-sidebar-pages.tsx`, R324), so the page is out of reach.
 * R167 dropped the Setup ROUTE, R340 folded the steps over this page, and both
 * still put an install screen in front of the product. The steps are gone.
 */
// `emptyOnly` pins the first-run empty view, whatever the store says: the
// store seeds forms, so /empty is the only way to reach that screen.
/**
 * R182: the link-form screen this page opens.
 *
 * 'cards' is the card screen Esteban drew in FigJam on 2026-09-01: three
 * checkable cards on the left, one setup pane on the right. 'wizard' is the
 * numbered wizard every round up to R181 built. Both files stand on disk, and
 * they take the same props, so this one word is the roll back he asked for.
 */
const LINK_FORM_SCREEN: 'cards' | 'wizard' = 'cards'
const LinkForm = LINK_FORM_SCREEN === 'cards' ? LinkFormCardsModal : LinkFormModal

export const FormSpamPage = ({
	emptyOnly = false
}: {
	emptyOnly?: boolean
}) => {
	const navigate = useNavigate()
	const {
		forms, addForm, updateForm, crmAccounts, api,
		loading, error, canWrite, refresh
	} = useFormSpam()
	// The account holds no form yet. The tiles would all
	// read 0 and the toolbar would filter nothing, so the whole list block
	// stands down and one empty state takes the page.
	const hasForms = !emptyOnly && forms.length > 0
	const [search, setSearch] = useState('')
	// Facet picks from the Filters menu, keyed by category id.
	const [filters, setFilters] = useState<Record<string, string[]>>({})
	const [skip, setSkip] = useState(0)
	const [pageSize, setPageSize] = useState(PAGE_SIZE)
	const [sort, setSort] = useState('')
	// Refresh has nothing to fetch in the lane, so it re-mounts the table: the
	// fade replays (the press is answered) and GTable's own page label returns
	// to 1 with the skip this button resets.
	const [refreshTick, setRefreshTick] = useState(0)
	const [sheetOpen, setSheetOpen] = useState(false)
	const [detailId, setDetailId] = useState<string | null>(null)
	// R105: how far back the page looks. It sits in the page header, where a
	// date picker normally sits, because it cuts the tray AND the three count
	// columns in the table. One control, one meaning, the whole page.
	const [range, setRange] = useState<DashboardRange>('7d')
	const reporting = useLeadFormStats(
		api, range === '7d' ? 7 : 30, undefined, forms
	)

	// R298 (Gustavo, 11 Sep, pin #100): the table card has two tabs, Forms and
	// Submissions. The active one sits in the URL as `?tab=`, the way the
	// Connections page keeps its own (`connections-page.tsx`, the same DS Tabs
	// inside a card), so a reload and a shared link open the same tab. `form`
	// rides along only with Submissions: it is the drawer's deep link into the
	// Form facet, and it has no meaning on the Forms tab.
	const [searchParams, setSearchParams] = useSearchParams()
	const tab = searchParams.get('tab') === 'submissions' ? 'submissions' : 'forms'
	const setTab = (value: string) => {
		const next = new URLSearchParams(searchParams)
		next.set('tab', value)
		if (value !== 'submissions') next.delete('form')
		setSearchParams(next, { replace: true })
	}
	// `tab` is what the Tabs control shows, so the press is answered at once.
	// `paintedTab` is what the body renders, and it LAGS by one exit: the
	// outgoing panel plays `form-spam-leave`, then the incoming one mounts and
	// plays `form-spam-panel`. R300: `useSwapKey` runs that lag (and swaps at
	// once under reduced motion), and `useSwapHold` pins the card's body on
	// the outgoing height across the swap, then eases it to the incoming one.
	// The body used to drop to the new table's 76px shell for a frame, throw
	// the page scroll to the top, and grow back from the stub.
	const { painted: paintedTab, leaving } = useSwapKey(tab)
	const panelHold = useSwapHold(paintedTab, leaving)
	// The drawer's "View submissions" link opens `?tab=submissions&form=<id>`.
	// The deep link seeds the Form facet once; the table owns the picks after
	// that, so the URL must not restore a stale pick on a reload. Once the
	// Submissions panel has painted, `form` leaves the URL. The table reads
	// `initialFormIds` only in its `useState` initialiser, so the strip does
	// not reset the pick it seeded.
	const formParam = searchParams.get('form')
	useEffect(() => {
		if (paintedTab !== 'submissions' || !formParam) return
		const next = new URLSearchParams(searchParams)
		next.delete('form')
		setSearchParams(next, { replace: true })
	}, [paintedTab, formParam, searchParams, setSearchParams])
	// R114: the stop in front of a pause. The drawer holds its own copy of this
	// hook for its own header button, and both read the same words.

	// Two states, one grid cell: the first-run empty state and the dashboard.
	// R341 took the third state (the install steps) out of this page.
	const list = useEnterExit(hasForms, { duration: 200 })
	const blank = useEnterExit(!hasForms && !loading && !error, { duration: 200 })
	// The range picker follows the list: a range over an empty account controls
	// nothing, so it keeps its own enter and exit and leaves the header.
	const rangePick = useEnterExit(hasForms, { duration: 200 })

	/**
	 * R105. The three counts the table shows, per form, inside the range.
	 *
	 * They were lifetime totals off the record (`submissions`, `invalidEmails`,
	 * `blocked`), so a range switch left them standing while the charts moved.
	 * They now read the same seeded verdict series the charts read, sliced to
	 * the same days, and the two can no longer disagree.
	 *
	 * `allow` is an approved submission, `challenge` is one we flagged, and
	 * `block` is a visitor we stopped. That is the mapping `getVerdictSeries`
	 * already builds the series from, so the 30-day totals still land on the
	 * record's own lifetime numbers.
	 */
	const rangeTotals = useMemo(() => {
		const days = RANGE_TABS.find((tab) => tab.key === range)?.days ?? 30
		const map = new Map<string, VerdictTotals>()
		forms.forEach((form) => {
			const counts = reporting.stats?.forms.find(row => row.form_id === form.id)
			map.set(form.id, api ? countsToVerdict(counts) : verdictTotals(getVerdictSeries(form).slice(-days)))
		})
		return map
	}, [forms, range, api, reporting.stats])

	const NO_TOTALS: VerdictTotals = { allow: 0, block: 0, challenge: 0, total: 0 }

	/** The range counts for one form. A form the store just added has none yet. */
	const totalsOf = (form: FormRecord): VerdictTotals =>
		rangeTotals.get(form.id) ?? NO_TOTALS

	// R142 (Gustavo, pin 42): the period BEFORE the range, per form, drawn the
	// way the drawer legend draws its own; see `previousVerdictTotals`.
	const rangePrev = useMemo(() => {
		const map = new Map<string, VerdictTotals>()
		rangeTotals.forEach((totals, id) => {
			const counts = reporting.stats?.forms.find(row => row.form_id === id)?.previous
			map.set(id, api ? countsToVerdict(counts) : previousVerdictTotals(totals, `${id}/verdict/${range}`))
		})
		return map
	}, [rangeTotals, range, api, reporting.stats])

	const prevOf = (form: FormRecord): VerdictTotals =>
		rangePrev.get(form.id) ?? NO_TOTALS

	const lastSubmissionOf = (form: FormRecord) => {
		if (!api) return form.lastSeen
		const last = reporting.stats?.forms.find(row => row.form_id === form.id)?.last_submission_at
		return last ? formatDistanceToNow(new Date(last), { addSuffix: true }) : null
	}

	const rows = useMemo(() => {
		const needle = search.trim().toLowerCase()
		return forms.filter(
			(form) =>
				matchesFilters(form, filters, crmAccounts) &&
				(!needle ||
					form.name.toLowerCase().includes(needle) ||
					form.url.toLowerCase().includes(needle))
		)
	}, [forms, search, filters, crmAccounts])

	// R150 (audit 2026-08-28): the size of the set BEFORE the search. `rows`
	// shrinks with every keystroke, and a key on its length re-mounted the
	// table on most of them, which is the remount the comment on the key
	// says the search no longer causes. The set changes only with the filters
	// or a settings change in the drawer, and that is what the key reads.
	const setSize = useMemo(
		() => forms.filter((form) => matchesFilters(form, filters, crmAccounts)).length,
		[forms, filters, crmAccounts]
	)

	const activeFilterCount = Object.values(filters).reduce(
		(total, ids) => total + ids.length,
		0
	)

	// The trigger badge enters and exits instead of hard-cutting, and the
	// rightIcon prop unmounts with it: AppButton renders the 16px icon slot
	// whenever the prop is truthy, so a badge that renders null at count 0
	// still left a hole after the label. The last positive count is held for
	// the exit so CountBadge does not null out mid-exit.
	const filterBadge = useEnterExit(activeFilterCount > 0, { duration: 150 })
	const lastFilterCount = useRef(1)
	useEffect(() => {
		if (activeFilterCount > 0) lastFilterCount.current = activeFilterCount
	}, [activeFilterCount])

	// The header sorts like the Custom Rules table: the parent holds the sort
	// string (`key` or `-key`), orders the rows, and then slices the page.
	const sorted = useMemo(() => {
		if (!sort) return rows
		const key = sort.startsWith('-') ? sort.slice(1) : sort
		const dir = sort.startsWith('-') ? -1 : 1
		const rank = (form: FormRecord): string | number => {
			if (key === 'name') return form.name.toLowerCase()
			if (key === 'botCheck') {
				// Rank by how hard the check acts on a visitor: Off, Monitoring, On.
				const state = botCheckState(form)
				return state === 'on' ? 2 : state === 'monitoring' ? 1 : 0
			}
			if (key === 'emailValidation') return form.emailValidation ? 1 : 0
			if (key === 'crmFlag') {
				// R294: Off, then the broken login, then On, the same "how much
				// reaches the CRM" order the bot column uses for its states.
				const state = crmFlagState(form, crmAccounts)
				return state === 'on' ? 2 : state === 'lost' ? 1 : 0
			}
			// R105: the three count columns rank on the RANGE number, not on the
			// lifetime one. A sort that ordered by a number the cell does not show
			// would read as a broken sort.
			if (key === 'submissions') return totalsOf(form).total
			if (key === 'invalidEmails') return totalsOf(form).challenge
			return totalsOf(form).block
		}
		return [...rows].sort((a, b) => {
			const va = rank(a)
			const vb = rank(b)
			if (typeof va === 'string' && typeof vb === 'string')
				return va.localeCompare(vb) * dir
			return ((va as number) - (vb as number)) * dir
		})
	}, [rows, sort, rangeTotals, crmAccounts])

	// GTable renders the page it is handed. It does not slice, so the parent does.
	const page = sorted.slice(skip, skip + pageSize)

	// R70: the table wrapper used to fade IN on a key change with no exit, so a
	// filter press dropped the old rows in the same frame and left a hole while
	// the new ones arrived. `useSwapKey` lags the painted key by one 150ms exit.
	//
	// The rows must lag with it. `page` is already the NEW slice on the render
	// that starts the exit, so the outgoing table would paint the new rows and
	// then fade THEM out. `lastPainted` records what actually committed, and
	// the table reads that copy while the exit plays.
	// R179: pageSize sits in the key, so a picker change swaps with the fade.
	// (R252: the page label lives in the DS Pagination now and follows skip.)
	const tableKey = `${JSON.stringify(filters)}-${setSize}-${pageSize}-${refreshTick}`
	const { painted: paintedTableKey, leaving: tableLeaving } = useSwapKey(tableKey)
	// R114: the fade alone was not the whole swap. A facet with fewer rows makes
	// the card shorter, and the card used to lose that height in one frame at
	// the commit, so the page under it jumped. The frame eases the box instead.
	const { inner: swapInner, height: swapHeight } = useFlowHeight()
	const lastPainted = useRef({ page, total: rows.length })
	useEffect(() => {
		if (!tableLeaving) lastPainted.current = { page, total: rows.length }
	})
	const shownPage = tableLeaving ? lastPainted.current.page : page
	const shownTotal = tableLeaving ? lastPainted.current.total : rows.length

	// R252 (Esteban, 3 Sep): "make this into the actual canonical pagination
	// thing - the visitor tracking one does have it". The footer is the DS
	// `Pagination` (Figma "_Pagination"), the one Visitor Tracking renders:
	// native rows-per-page select, "Page X of Y", four nav buttons. It
	// supersedes R179's react-select picker inside GTable's own footer, which
	// is now off. `skip` and `pageSize` drive this one; the page number is
	// derived, so no remount is needed to reset a label.
	const totalPages = Math.max(1, Math.ceil(shownTotal / pageSize))
	const currentPage = Math.min(Math.floor(skip / pageSize) + 1, totalPages)
	// GTable dropped its footer in the same frame the view emptied. This one
	// holds through a 150ms fade and unmounts after.
	const pager = useEnterExit(shownTotal > 0, { duration: 150 })

	// The row set can shrink under a nonzero skip: unlink the last row on page 2,
	// or pause the only Live form. The slice then answers empty, and GTable hides
	// the pager on an empty table, so nothing on screen can bring the user back.
	useEffect(() => {
		if (skip > 0 && skip >= rows.length) setSkip(0)
	}, [rows.length, skip])

	const columns = useMemo<GTableColumn<FormRecord>[]>(
		() => [
			{
				label: 'Form',
				sortKey: 'name',
				className: 'justify-start',
				parentClassName: 'z-10 cg-bg-canvas whitespace-nowrap',
				// Pinned left, like the clicks table. The two lines truncate.
				// R275 (Gustavo, pin #70): 184px left 136px for the text after the
				// 24px cell padding, and "demo.example.com/demo" runs to 167px at
				// 14px, so even the short URLs cut off after the domain. 264px leaves
				// 216px: paths up to about 30 characters ("/pricing/trial", 205px)
				// show whole and only the long ones ("/resources/ebook", 237px)
				// truncate, which is the balance he asked for. The URL stays at
				// 14px because the DS ListItem keeps its secondary line at text-sm.
				// The 1170px budget the old comment named no longer holds: the nine
				// columns run to 1496px since R123, so the table already scrolls.
				freeze: true,
				width: '264px',
				render: (form) => (
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-[14px] font-medium leading-[18px] cg-text-text-primary">
							{form.name}
						</span>
						<div className="min-w-0 [&>div]:max-w-full">
							<Tooltip content={form.url} portal>
								<span className="cursor-default truncate text-[14px] font-normal leading-[18px] cg-text-text-secondary">
									{form.url}
								</span>
							</Tooltip>
						</div>
					</div>
				)
			},
			{
				// R123 (Gustavo): the list names when each form last heard from a
				// visitor, the way the drawer head does. The mock keeps a relative
				// string, so the column does not sort; a sort on "2 min ago" would
				// order by the alphabet and read as broken.
				label: 'Last submission',
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => (
					<span className="whitespace-nowrap text-[14px] leading-[18px] cg-text-text-primary">
						{/* R313 (Gustavo, 11 Sep): "the 'No traffic yet' should instead
						    just have a '–' until there's submissions". A dash in the
						    secondary ink; the words stay for screen readers. */}
						{lastSubmissionOf(form) ?? (
							<>
								<span aria-hidden="true" className="cg-text-text-secondary">
									–
								</span>
								<span className="sr-only">No submissions yet</span>
							</>
						)}
					</span>
				)
			},
			// R291 (Esteban, 10 Sep): "get rid of this column too". The Protection
			// status column (R116, R137, R260, R290) sat here, between Last
			// submission and the two protection columns. It read Active, Needs
			// setup or Paused from `protectionMeta`. It left with the Needs setup
			// pill and the Status filter group. The drawer header badge (R260) and
			// its red setup lines (R272) still read `needsSetup`.
			//
			// R294 (Gustavo, 4 Sep, pin #74): the three counts come BEFORE the
			// three protection columns. "The number columns could be positioned
			// before the on/off ones, given that they might be more important."
			// Up to R293 the switches came first.
			{
				// R123 (Gustavo): the three counts read as ONE total and its two bad
				// slices. `Approved` counted only the kept share, and a reader who
				// added the three columns got a number that matched nothing on the
				// page. The first column is now the whole form, the way the drawer's
				// first tile is.
				label: 'Form submissions',
				sortKey: 'submissions',
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => (
					api && !reporting.stats ? (
						reporting.loading ? <DotMatrix size="xs" label="Loading counts" /> :
						<span aria-label="Counts unavailable">–</span>
					) : <CountCell value={totalsOf(form).total} prev={prevOf(form).total} />
				)
			},
			{
				label: 'Flagged submissions',
				sortKey: 'invalidEmails',
				info: {
					title: 'Flagged submissions',
					// R294 (Gustavo, 10 Sep, pin #98): "should be the ones flagged as
					// Invalid, Spam trap or Do not mail". R251 named Catch-all, Do not
					// mail and Unknown here; those are the sheet's other values.
					description:
						'Submissions flagged by the form protection settings in effect when they were received.'
				},
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => (
					api && !reporting.stats ? (
						reporting.loading ? <DotMatrix size="xs" label="Loading counts" /> :
						<span aria-label="Counts unavailable">–</span>
					) : <CountCell value={totalsOf(form).challenge} prev={prevOf(form).challenge} />
				)
			},
			{
				label: 'Blocked bots',
				sortKey: 'blocked',
				info: {
					title: 'Blocked bots',
					description:
						'Submissions blocked for a high bot probability or a failed CAPTCHA.'
				},
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => (
					api && !reporting.stats ? (
						reporting.loading ? <DotMatrix size="xs" label="Loading counts" /> :
						<span aria-label="Counts unavailable">–</span>
					) : <CountCell value={totalsOf(form).block} prev={prevOf(form).block} />
				)
			},
			{
				// R251 (Esteban, 3 Sep): "These headers should match whatever's in
				// the modal". The protection columns and their filter groups carried
				// the wizard's substep names, his verbatim `CARDS` copy in the cards
				// modal: the rail label as the header, the pane title and its line as
				// the info tooltip.
				//
				// R294 (Gustavo, 4 Sep, pin #72): the headers read his three short
				// names, "Block bots", "Protect Ads" and "Flag on CRM". The info
				// tooltips keep the wizard's pane title and line, so the long copy is
				// one hover away. The CRM column is new; R251 had none.
				label: 'Block bots',
				sortKey: 'botCheck',
				info: {
					title: 'Block bot form submissions',
					// R321: mirrors `CARDS[2].desc` in the panes.
					description: "Every visitor's bot probability is scored before form submission is attempted."
				},
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => <CheckCell state={botCheckState(form)} />
			},
			{
				label: 'Protect Ads',
				sortKey: 'emailValidation',
				info: {
					title: 'Block spammers from seeing my PPC Ads',
					description: 'Prevent your ads from being shown again to users that submit invalid emails'
				},
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => (
					<CheckCell state={form.emailValidation ? 'on' : 'off'} />
				)
			},
			{
				// R294 (Gustavo, pins #72 and #74): the CRM substep's column. On and
				// Off follow the drawer's switch through `crmFlagState`. A form whose
				// CRM login the provider revoked reads Access lost, in the drawer's
				// red, so the list carries a row with an issue in the cell that owns
				// it, with no pill and no status column. "Contact us" is that seed.
				label: 'Flag on CRM',
				sortKey: 'crmFlag',
				info: {
					title: 'Flag invalid submissions on my CRM',
					description: 'Integrate ClickGuard to your CRM and flag spam leads'
				},
				className: 'justify-start',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (form) => <CheckCell state={crmFlagState(form, crmAccounts)} />
			},
			{
				label: '',
				className: 'justify-end',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				freezeRight: true,
				render: (form) => (
					<div className="flex items-center gap-2">
						<LostFlag lost={crmFlagState(form, crmAccounts) === 'lost'} />
						<AppButton
							variant="secondary"
							size="sm"
							label="View"
							rightIcon={<ChevronRight className="h-4 w-4" />}
							onClick={() => setDetailId(form.id)}
						/>
					</div>
				)
			}
		],
		// R105: `rangeTotals` belongs here. The three count cells read it through
		// `totalsOf`, so a memo that skips it keeps the first range on screen for
		// ever. Measured on 2026-08-25: the legend moved and the cells did not.
		[updateForm, rangeTotals, rangePrev, crmAccounts, api, reporting.stats, reporting.loading]
	)

	const onLink = async (draft: NewFormDraft) => {
		const record = await addForm(draft)
		setSheetOpen(false)
		// /empty pins the first-run view, so the new form has nowhere to appear.
		// Hand the user back to the real list instead of a screen that still
		// says the account is empty.
		if (emptyOnly) {
			navigate('..')
			return
		}
		setDetailId(record.id)
	}

	// No Installation action here on purpose. The install steps fold down over
	// this page while the code is not live (R167, R340), and two doors to one
	// flow is one door too many.
	//
	// R105: the range sits in the `actions` slot, which the DS reserves for a
	// date picker or a filter. It reads every number under it, the tray charts
	// and the three count columns, so it belongs to the page and not to a card.
	//
	// It follows the list, because a range over an account with no form and no
	// code says nothing. It shares the list enter and exit, so it never cuts.
	const header = (
		<PageHeader
			title="Lead Forms"
			subtitle="Setup bot and spam protection for your lead generation forms."
			actions={
				rangePick.mounted && hasForms ? (
					<div
						className={`form-spam-enter form-spam-fade ${
							hasForms ? '' : 'form-spam-fade--out'
						}`}
					>
						<SegmentedTabs
							size="regular"
							value={range}
							onChange={(next) => setRange(next as DashboardRange)}
						>
							<SegmentedTabList>
								{RANGE_TABS.map((tab) => (
									<SegmentedTab
										key={tab.key}
										value={tab.key}
										label={tab.label}
									/>
								))}
							</SegmentedTabList>
						</SegmentedTabs>
					</div>
				) : undefined
			}
		/>
	)

	return (
		<>
			<PageTitle pageName="Lead Forms" />
			<FormSpamShell header={header}>
				{/* Both crossfade halves share ONE grid cell. In flow they would
				    stack, and the incoming half would render a card's height too
				    low for 200ms and then snap up. `items-start` keeps each half at
				    its own height. */}
				{(error || reporting.error) && (
					<div role="alert" className="flex items-center gap-3 cg-text-danger">
						{error || reporting.error}
						<AppButton size="sm" variant="secondary" label="Retry"
							onClick={() => { void refresh(); reporting.refresh() }} />
					</div>
				)}
				{loading && !hasForms && (
					<div className="flex flex-1 items-center justify-center" role="status">
						<DotMatrix size="lg" label="Loading lead forms" />
					</div>
				)}
				<div className="grid grid-cols-1 items-start">
				{/* First run: the account holds no form. Same grid cell as the
				    other half, so the swap to the real list crossfades in place
				    and nothing jumps. */}
				{blank.mounted && (
					<div
						className={`[grid-area:1/1] form-spam-enter form-spam-fade ${
							!hasForms ? '' : 'form-spam-fade--out'
						}`}
					>
						<FormSpamEmptyState
							icon={
								<FileCheck02
									className="h-12 w-12 cg-text-brand"
									strokeWidth={1}
								/>
							}
							title="No forms linked yet"
							description="Click the button below to start protecting your forms from fake leads, bots and invalid submissions."
							actionLabel="Link your first form"
							onAction={canWrite ? () => setSheetOpen(true) : undefined}
							hint="Link a form and choose its checks: bot verification and email validation. Install the widget once. We score each submission and flag or block the bad ones."
						/>
					</div>
				)}

				{list.mounted && (
					/* R206 (Esteban, 1 Sep): 12px between the chart tray and the
					   table card, not 20px. */
					<div
						className={`[grid-area:1/1] flex flex-col gap-3 form-spam-enter form-spam-fade ${
							hasForms ? '' : 'form-spam-fade--out'
						}`}
					>
						{/* R104: the account's traffic quality, over the list it
						    summarises. The Over time / Breakdown switch sits
						    inside the tray header band, so the reader can see it
						    reaches nothing below the tray. R105: the range comes
						    from the page header, because it reaches the table too. */}
						<TrafficQualityTray range={range} />

						<div className="flex flex-col gap-3 overflow-hidden rounded-xl border cg-border-border-default cg-bg-surface p-3 shadow-[0px_4px_16px_-2px_rgba(16,24,40,0.03),0px_1px_4px_-2px_rgba(16,24,40,0.02)]">
							{/* R298 (Gustavo, 11 Sep, pin #100): two tabs at the top of the
							    card. Forms is the table below, unchanged. Submissions is one
							    table of every form's rows, the feed the drawer used to hold.
							    The precedent is the Connections page (`connections-page.tsx`):
							    the same DS Tabs inside a card, the active tab in `?tab=`. No
							    TabPanel: it would hard-cut, so the body crossfades itself
							    through `paintedTab` and `leaving`, one panel mounted at a
							    time. */}
							<Tabs size="sm" value={tab} onChange={setTab}>
								<TabList fullBleed>
									<Tab value="forms" label="Forms" />
									<Tab value="submissions" label="Submissions" />
								</TabList>
							</Tabs>
							<div
								ref={panelHold.frame}
								// R300: pinned across a tab swap only, `auto` the rest of
								// the time, so the tables' own frames run their in-table
								// swaps without a second box over them.
								style={
									panelHold.height === undefined
										? undefined
										: { height: panelHold.height }
								}
								// R313 (Gustavo, 11 Sep): "The search bar focus inside the
								// tables is bad like clipped". At rest this box starts at
								// the same x/y as the search Input, so a permanent
								// `overflow-hidden` shaved the top and left of its 2px DS
								// focus ring. Clip only while the height is pinned for a
								// tab swap, which is the only time there is overflow to hide.
								className={`form-spam-swap-frame${
									panelHold.height === undefined ? '' : ' overflow-hidden'
								}`}
							>
							<div ref={panelHold.inner}>
							{paintedTab === 'forms' ? (
							<div
								key="forms"
								// `--still`: the frame around this one eases the height, and
								// a 4px rise on top of that reads as a lurch.
								className={`flex flex-col gap-3 ${
									leaving
										? 'form-spam-leave form-spam-leave--still'
										: 'form-spam-panel form-spam-panel--still'
								}`}
							>
							<div className="flex items-center gap-2.5">
								{/* flex-wrap is the narrow-window guard. When the card cannot
								    give Search, Filters and the pills their width, the Refresh
								    button would land on top of the last pill without the wrap.
								    The strip drops to a second line instead, and Refresh and
								    Link new form hold their place. */}
								<div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
									{/* [&_span]: pulls the DS leading-icon span's cg-pl-3.5 (14px)
									    back to 6px, so the glyph sits the same distance from the
									    left border as the text does from the top and bottom ones;
									    !border lightens the wrapper's border-strong to match the
									    toolbar buttons (twMerge can't dedupe cg-). */}
									<div className="w-[200px] [&_span[aria-hidden]]:!pl-1.5">
										<Input
											size="sm"
											state="default"
											className="[&:not(:focus-within)]:!border-[rgb(var(--cg-border))]"
											// 6px vertical padding lands the field at 32px (18px
											// text line + 2px wrapper border), flush with the sm
											// AppButtons — the DS sm size's py-2 renders 36px.
											style={{ paddingTop: 6, paddingBottom: 6 }}
											placeholder="Search forms…"
											// 14px, not the DS default 16px: at 32px tall the glyph
											// reads heavier than the 14px placeholder next to it.
											leadingIcon={<SearchMd className="h-3.5 w-3.5" />}
											autoComplete="off"
											value={search}
											onChange={(e) => {
												setSearch(e.target.value)
												setSkip(0)
											}}
										/>
									</div>
									<TwoPaneMenu
										className="form-spam-facet"
										selectionMode="multiple"
										twoTone
										showSearch={false}
										categories={FILTER_CATEGORIES}
										selectedItemIds={filters}
										onSelectionChange={(next) => {
											setFilters(next)
											setSkip(0)
										}}
										categoriesFooter={
											<div className="px-2 pb-2">
												<Divider />
												<ListItem
													as="button"
													// 8px, to match the 8px the pane's own p-2 puts ABOVE the
													// rule. mt-1 gave 4, so the rule sat closer to Clear filters
													// than to the last filter. Same space each side of a rule.
													className="mt-2"
													label={<span className="cg-text-brand">Clear filters</span>}
													disabled={activeFilterCount === 0}
													onClick={() => {
														setFilters({})
														setSkip(0)
													}}
												/>
											</div>
										}
										trigger={
											<AppButton
												variant="secondary"
												size="sm"
												label="Filters"
												leftIcon={<FilterLines className="h-4 w-4" />}
												rightIcon={
													filterBadge.mounted ? (
														<CountBadge
															count={
																activeFilterCount > 0
																	? activeFilterCount
																	: lastFilterCount.current
															}
															className={`transition duration-150 motion-reduce:transition-none ${
																filterBadge.entered
																	? 'scale-100 opacity-100'
																	: 'scale-75 opacity-0'
															}`}
														/>
													) : undefined
												}
											/>
										}
									/>
									{/* R291 (Esteban, 10 Sep): the Needs setup pill (R110, R116, R145)
									    left with the Protection status column. The search box and the
									    Filters menu are the two controls that cut the table. */}
								</div>
								<AppButton
									variant="secondary"
									size="sm"
									label="Refresh"
									leftIcon={<RefreshCw01 className="h-4 w-4" />}
									onClick={() => {
										setSkip(0)
										setRefreshTick((tick) => tick + 1)
										void refresh()
										reporting.refresh()
									}}
								/>
								<AppButton
									variant="primary"
									size="sm"
									label="Link new form"
									disabled={!canWrite}
									leftIcon={<Plus className="h-4 w-4" />}
									onClick={() => setSheetOpen(true)}
								/>
							</div>

							{/* Keyed on the filter AND the row count, so a set change fades
							    in instead of a hard cut (a settings change in the drawer can
							    drop a row from a filtered view).

							    The raw search string is NOT in the key. It used to be, and
							    every character re-mounted the table: the 180ms fade replayed
							    per keystroke and the scroll jumped. The `search` prop below
							    returns GTable's page label to 1 without a re-mount, so the
							    key only has to answer real set changes. */}
							<div
								// The height is inline and it follows the measured contents.
								// Hide the overflow with it: a shorter box must clip the rows
								// it is losing, not paint them past its own edge.
								style={swapHeight === undefined ? undefined : { height: swapHeight }}
								className="form-spam-swap-frame overflow-hidden"
							>
							{/* The measured element. It has to be a wrapper and not the keyed
							    panel itself, because the panel is a NEW element on every swap
							    and an observer on it would go with the old one. */}
							<div ref={swapInner}>
							<div
								key={paintedTableKey}
								// `--still` drops the 4px rise both keyframes carry. The frame
								// around this one is already easing its own height, and two
								// movements at once read as a lurch, not as one swap.
								className={`overflow-hidden rounded-t-lg ${
									tableLeaving
										? 'form-spam-leave form-spam-leave--still'
										: 'form-spam-panel form-spam-panel--still'
								}`}
							>
								<GTable
									variant="ds"
									containerClassName="rounded-none bg-transparent"
									columns={columns}
									data={shownPage}
									getRowId={(form) => form.id}
									// `search` stays as GTable's own reset hook. Since R252 the
									// page label lives in the DS Pagination below and follows
									// `skip`, which the search box resets as well.
									search={search}
									sort={sort}
									sortListener={(key) => {
										setSort(key)
										setSkip(0)
									}}
									totalCount={shownTotal}
									// R252: the footer is the DS Pagination below, not GTable's.
									showPagination={false}
									// The table is only empty because the search or the filters
									// emptied it: an account with no form at all never reaches
									// this table, the first-run empty state takes the page.
									emptyState={
										<div className="flex flex-col items-center gap-1 py-10 text-center">
											<span className="text-[14px] font-medium leading-[18px] cg-text-text-primary">
												No forms match this view
											</span>
											<span className="text-[13px] leading-[18px] cg-text-text-secondary">
												Clear the search, or link the form you want to protect.
											</span>
										</div>
									}
								/>
								{/* R252: the canonical footer sits where GTable's nav sat, in
								    the keyed panel, so it swaps with the rows. Held through its
								    150ms exit when the view empties. */}
								{pager.mounted ? (
									<div
										className={`transition-opacity duration-150 motion-reduce:transition-none ${
											pager.entered ? 'opacity-100' : 'opacity-0'
										}`}
									>
										<Pagination
											page={currentPage}
											totalPages={totalPages}
											onPageChange={(next) => setSkip((next - 1) * pageSize)}
											pageSize={pageSize}
											pageSizeOptions={PAGE_SIZE_OPTIONS}
											onPageSizeChange={(next) => {
												setPageSize(next)
												setSkip(0)
											}}
										/>
									</div>
								) : null}
							</div>
							</div>
							</div>
							</div>
							) : (
								<div
									key="submissions"
									className={`flex flex-col gap-3 ${
										leaving
											? 'form-spam-leave form-spam-leave--still'
											: 'form-spam-panel form-spam-panel--still'
									}`}
								>
									<SubmissionsTable
										forms={forms}
										range={range}
										initialFormIds={formParam ? [formParam] : undefined}
									/>
								</div>
							)}
							</div>
							</div>
						</div>
					</div>
				)}
				</div>
			</FormSpamShell>

			{/* R182: one constant picks the screen. `LinkFormCardsModal` is the
			    card screen from Esteban's FigJam draft (2026-09-01);
			    `LinkFormModal` is the numbered wizard, untouched on disk. Flip
			    LINK_FORM_SCREEN back to 'wizard' to roll the card screen back. */}
			<LinkForm
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				onLink={onLink}
				forceFirstRun={emptyOnly}
			/>

			<FormDetailDrawer
				formId={detailId}
				onClose={() => setDetailId(null)}
				// R298 (Gustavo, 11 Sep, pin #100): the drawer's "View submissions"
				// link closes the drawer and opens the Submissions tab with this
				// form picked in the Form facet. The id rides in the URL so the
				// table, which mounts after the tab swap, can read it.
				onViewSubmissions={(id) => {
					setDetailId(null)
					const next = new URLSearchParams(searchParams)
					next.set('tab', 'submissions')
					next.set('form', id)
					setSearchParams(next, { replace: true })
				}}
			/>

		</>
	)
}
